import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ThumbsUp, ThumbsDown, Plus, Link2, ExternalLink, Clock, CheckCircle, XCircle, TrendingUp, User, AlertTriangle } from 'lucide-react';
import { useClubSettings } from '../context/ClubSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'general', label: 'General' },
  { value: 'training', label: 'Training' },
  { value: 'health', label: 'Health' },
  { value: 'nutrition', label: 'Nutrition' },
  { value: 'grooming', label: 'Grooming' },
  { value: 'behavior', label: 'Behavior' },
  { value: 'news', label: 'News' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'rescue', label: 'Rescue & Adoption' }
];

function SubmissionCard({ submission, onVote, isVoting, currentUserId, voteSettings = {} }) {
  const netVotes = submission.upvotes - submission.downvotes;
  const isOwner = submission.submitted_by_id === currentUserId;
  const emojiUp = voteSettings.vote_emoji_up || '👍';
  const emojiDown = voteSettings.vote_emoji_down || '👎';
  
  return (
    <Card className="mb-4" data-testid={`submission-card-${submission.id}`}>
      <CardContent className="pt-4">
        <div className="flex gap-4">
          {/* Voting Column */}
          <div className="flex flex-col items-center gap-1 min-w-[60px]">
            <Button
              variant={submission.user_vote === 1 ? "default" : "ghost"}
              size="sm"
              onClick={() => onVote(submission.id, submission.user_vote === 1 ? 0 : 1)}
              disabled={isVoting || isOwner}
              className="h-8 w-8 p-0 text-lg"
              data-testid={`upvote-${submission.id}`}
            >
              {submission.user_vote === 1 ? <span>{emojiUp}</span> : <ThumbsUp className="h-4 w-4" />}
            </Button>
            <span className={`font-bold text-lg ${netVotes > 0 ? 'text-green-600' : netVotes < 0 ? 'text-red-600' : 'text-gray-500'}`}>
              {netVotes}
            </span>
            <Button
              variant={submission.user_vote === -1 ? "destructive" : "ghost"}
              size="sm"
              onClick={() => onVote(submission.id, submission.user_vote === -1 ? 0 : -1)}
              disabled={isVoting || isOwner}
              className="h-8 w-8 p-0 text-lg"
              data-testid={`downvote-${submission.id}`}
            >
              {submission.user_vote === -1 ? <span>{emojiDown}</span> : <ThumbsDown className="h-4 w-4" />}
            </Button>
            <span className="text-xs text-gray-500">{submission.total_votes} votes</span>
          </div>
          
          {/* Content */}
          <div className="flex-1">
            <div className="flex items-start gap-3">
              {submission.thumbnail_url && (
                <img 
                  src={submission.thumbnail_url} 
                  alt={submission.title}
                  className="w-24 h-16 object-cover rounded"
                />
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">
                  <a 
                    href={submission.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-blue-600 flex items-center gap-1"
                  >
                    {submission.title}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </h3>
                {submission.description && (
                  <p className="text-gray-600 text-sm mb-2 line-clamp-2">{submission.description}</p>
                )}
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <Badge variant="outline">{submission.category}</Badge>
                  <Badge variant="secondary">{submission.source_type.replace('_', ' ')}</Badge>
                  <span>by {submission.submitted_by_username}</span>
                  <span>{new Date(submission.submitted_at).toLocaleDateString()}</span>
                  {submission.status === 'approved' && (
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" /> Approved
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SubmitDialog({ open, onOpenChange, onSubmit, isSubmitting, postingLimits }) {
  const [formData, setFormData] = useState({
    url: '',
    title: '',
    description: '',
    category: 'general'
  });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.url || !formData.title) {
      toast.error('URL and title are required');
      return;
    }
    onSubmit(formData);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Submit a URL</DialogTitle>
        </DialogHeader>
        
        {postingLimits && !postingLimits.can_submit ? (
          <div className="text-center py-4">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-2" />
            <p className="text-gray-600">{postingLimits.reason}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="url">URL *</Label>
              <Input
                id="url"
                type="url"
                placeholder="https://..."
                value={formData.url}
                onChange={(e) => setFormData({...formData, url: e.target.value})}
                required
                data-testid="submit-url-input"
              />
            </div>
            
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="Give it a descriptive title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                maxLength={200}
                required
                data-testid="submit-title-input"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional: Why is this worth sharing?"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                maxLength={500}
                rows={3}
                data-testid="submit-description-input"
              />
            </div>
            
            <div>
              <Label htmlFor="category">Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(val) => setFormData({...formData, category: val})}
              >
                <SelectTrigger data-testid="submit-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {postingLimits && (
              <p className="text-sm text-gray-500">
                Submissions remaining today: {postingLimits.remaining_today} / {postingLimits.daily_limit}
              </p>
            )}
            
            <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="submit-url-button">
              {isSubmitting ? 'Submitting...' : 'Submit URL'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReputationCard({ reputation }) {
  if (!reputation) return null;
  
  const repColor = reputation.reputation_score >= 0 ? 'text-green-600' : 'text-red-600';
  
  return (
    <Card className="mb-6" data-testid="reputation-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <User className="h-5 w-5" /> Your Reputation
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className={`text-2xl font-bold ${repColor}`}>{reputation.reputation_score}</div>
            <div className="text-xs text-gray-500">Score</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{reputation.total_submissions}</div>
            <div className="text-xs text-gray-500">Submissions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{reputation.approved_submissions}</div>
            <div className="text-xs text-gray-500">Approved</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{reputation.approval_rate}%</div>
            <div className="text-xs text-gray-500">Approval Rate</div>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-1">
            <span>Today's submissions</span>
            <span>{reputation.submissions_today} / {reputation.daily_limit}</span>
          </div>
          <Progress value={(reputation.submissions_today / reputation.daily_limit) * 100} className="h-2" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function UserSubmissions() {
  const navigate = useNavigate();
  const { voteSettings } = useClubSettings();
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [currentUserId, setCurrentUserId] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [viralSubmissions, setViralSubmissions] = useState([]);
  const [reputation, setReputation] = useState(null);
  const [postingLimits, setPostingLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVoting, setIsVoting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [sortBy, setSortBy] = useState('newest');
  const [category, setCategory] = useState('all');
  
  useEffect(() => {
    // Get current user ID
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setCurrentUserId(user.id);
      } catch (e) {
        console.error('Failed to parse user data:', e);
      }
    }
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, sortBy, category, token]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Fetch submissions based on active tab
      let status = activeTab === 'viral' ? null : activeTab;
      let endpoint = activeTab === 'viral' 
        ? `${API_URL}/api/submissions/viral`
        : activeTab === 'mine'
        ? `${API_URL}/api/submissions/my-submissions`
        : `${API_URL}/api/submissions/urls?status=${status}&sort_by=${sortBy}${category !== 'all' ? `&category=${category}` : ''}`;
      
      const submissionsRes = await fetch(endpoint, { headers });
      if (submissionsRes.ok) {
        const data = await submissionsRes.json();
        if (activeTab === 'viral') {
          setViralSubmissions(data);
        } else if (activeTab === 'mine') {
          setMySubmissions(data);
        } else {
          setSubmissions(data);
        }
      }
      
      // Fetch reputation if logged in
      if (token) {
        const repRes = await fetch(`${API_URL}/api/submissions/my-reputation`, { headers });
        if (repRes.ok) {
          const repData = await repRes.json();
          setReputation(repData);
          setPostingLimits({
            can_submit: repData.can_submit,
            reason: repData.remaining_today === 0 ? 'Daily limit reached' : null,
            remaining_today: repData.remaining_today,
            daily_limit: repData.daily_limit
          });
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleVote = async (submissionId, voteValue) => {
    if (!token) {
      toast.error('Please log in to vote');
      return;
    }
    
    setIsVoting(true);
    try {
      const res = await fetch(`${API_URL}/api/submissions/urls/${submissionId}/vote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ vote: voteValue })
      });
      
      if (res.ok) {
        const result = await res.json();
        // Update the submission in state
        setSubmissions(prev => prev.map(s => 
          s.id === submissionId 
            ? { ...s, upvotes: result.upvotes, downvotes: result.downvotes, total_votes: result.total_votes, user_vote: result.user_vote }
            : s
        ));
        toast.success('Vote recorded!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to vote');
      }
    } catch (err) {
      toast.error('Error voting');
    } finally {
      setIsVoting(false);
    }
  };
  
  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/submissions/urls`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success('URL submitted successfully!');
        setSubmitDialogOpen(false);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to submit');
      }
    } catch (err) {
      toast.error('Error submitting URL');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const displaySubmissions = activeTab === 'viral' ? viralSubmissions : activeTab === 'mine' ? mySubmissions : submissions;
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Community Submissions</h1>
            <p className="text-gray-600">Submit and vote on URLs to add to our curated content feed</p>
          </div>
          {token && (
            <Button onClick={() => setSubmitDialogOpen(true)} data-testid="open-submit-dialog">
              <Plus className="h-4 w-4 mr-2" /> Submit URL
            </Button>
          )}
        </div>
        
        {token && <ReputationCard reputation={reputation} />}
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <TabsList>
              <TabsTrigger value="pending" data-testid="tab-pending">
                <Clock className="h-4 w-4 mr-1" /> Pending
              </TabsTrigger>
              <TabsTrigger value="approved" data-testid="tab-approved">
                <CheckCircle className="h-4 w-4 mr-1" /> Approved
              </TabsTrigger>
              <TabsTrigger value="viral" data-testid="tab-viral">
                <TrendingUp className="h-4 w-4 mr-1" /> Trending
              </TabsTrigger>
              {token && (
                <TabsTrigger value="mine" data-testid="tab-mine">
                  <User className="h-4 w-4 mr-1" /> My Submissions
                </TabsTrigger>
              )}
            </TabsList>
            
            {activeTab !== 'viral' && activeTab !== 'mine' && (
              <div className="flex gap-2">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="popular">Popular</SelectItem>
                    <SelectItem value="controversial">Controversial</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <TabsContent value="pending">
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : displaySubmissions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Link2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No pending submissions</h3>
                  <p className="text-gray-600 mb-4">Be the first to submit a URL!</p>
                  {token && (
                    <Button onClick={() => setSubmitDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" /> Submit URL
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              displaySubmissions.map(sub => (
                <SubmissionCard 
                  key={sub.id} 
                  submission={sub} 
                  onVote={handleVote}
                  isVoting={isVoting}
                  currentUserId={currentUserId}
                  voteSettings={voteSettings}
                />
              ))
            )}
          </TabsContent>
          
          <TabsContent value="approved">
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : displaySubmissions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold">No approved submissions yet</h3>
                </CardContent>
              </Card>
            ) : (
              displaySubmissions.map(sub => (
                <SubmissionCard 
                  key={sub.id} 
                  submission={sub} 
                  onVote={handleVote}
                  isVoting={isVoting}
                  currentUserId={currentUserId}
                  voteSettings={voteSettings}
                />
              ))
            )}
          </TabsContent>
          
          <TabsContent value="viral">
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : viralSubmissions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold">No trending content yet</h3>
                  <p className="text-gray-600">Content that receives exceptional engagement will appear here</p>
                </CardContent>
              </Card>
            ) : (
              viralSubmissions.map(sub => (
                <SubmissionCard 
                  key={sub.id} 
                  submission={sub} 
                  onVote={handleVote}
                  isVoting={isVoting}
                  currentUserId={currentUserId}
                  voteSettings={voteSettings}
                />
              ))
            )}
          </TabsContent>
          
          <TabsContent value="mine">
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : mySubmissions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold">You haven't submitted anything yet</h3>
                  <p className="text-gray-600 mb-4">Share great pet content with the community!</p>
                  <Button onClick={() => setSubmitDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Submit URL
                  </Button>
                </CardContent>
              </Card>
            ) : (
              mySubmissions.map(sub => (
                <SubmissionCard 
                  key={sub.id} 
                  submission={sub} 
                  onVote={handleVote}
                  isVoting={isVoting}
                  currentUserId={currentUserId}
                  voteSettings={voteSettings}
                />
              ))
            )}
          </TabsContent>
        </Tabs>
        
        {/* Info Card */}
        <Card className="mt-6 bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <h4 className="font-semibold text-blue-900 mb-2">How it works</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>Submit URLs to pet-related content (videos, articles, resources)</li>
              <li>Community members vote on submissions</li>
              <li>Content that reaches the vote threshold gets promoted to the curated feed</li>
              <li>Your reputation score increases when your submissions are approved</li>
              <li>Exceptional content that goes "viral" is featured across all clubs</li>
            </ul>
          </CardContent>
        </Card>
        
        <SubmitDialog
          open={submitDialogOpen}
          onOpenChange={setSubmitDialogOpen}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          postingLimits={postingLimits}
        />
      </div>
    </div>
  );
}
